import React, { useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, Button, IconButton, Stack, TextField, Typography } from "@mui/material";
import { useCharacteristics } from "hooks";
import { startCase } from "lodash";
import { useAccountSelector } from "store";
import { camelCaseToTitle } from "utils";

import { State } from "./CreateReportSection";
import { GroupedColumnsPicker } from "./GroupedColumnsPicker";

type SubGroupByOption = { type: keyof State["table"]["columns"]; value: string };

/** Always an object so callers don't branch on shape. measures/calcFields apply when "measure" is in groupBy. */
export type TableGroupByResult = {
  groupBy: string[];
  measures: string[];
  calcFields: string[];
};

function columnsToSubGroupBy(columns: State["table"]["columns"]): SubGroupByOption[] {
  const result: SubGroupByOption[] = [];
  (columns.measures || []).forEach((v) => result.push({ type: "measures", value: String(v) }));
  (columns.calcFields || []).forEach((v) => result.push({ type: "calcFields", value: String(v) }));
  return result;
}

function subGroupByToColumns(value: SubGroupByOption[]): Pick<State["table"]["columns"], "measures" | "calcFields"> {
  const measures: string[] = [];
  const calcFields: string[] = [];
  value.forEach((item) => {
    if (item.type === "measures") measures.push(item.value);
    if (item.type === "calcFields") calcFields.push(item.value);
  });
  return { measures, calcFields };
}

interface Props {
  groupBy: string[];
  columns?: State["table"]["columns"];
  onSubmit: (result: TableGroupByResult) => void;
  onCancel: () => void;
  groupsNumber?: number;
}

export const TableGroupBy: React.FC<Props> = ({ groupBy, columns, onSubmit, onCancel, groupsNumber = 3 }) => {
  const { support_investor_groups } = useAccountSelector() || {};

  const initialSubGroupBy = useMemo(
    () => (columns && groupBy.some((g) => g === "measure") ? columnsToSubGroupBy(columns) : []),
    [groupBy, columns]
  );

  const [state, setState] = useState<string[]>(groupBy);
  const [subGroupBy, setSubGroupBy] = useState<SubGroupByOption[]>(initialSubGroupBy);

  const handleGroupChange = (index: number, value: string | null) => {
    const newState = [...state];
    newState[index] = value ?? "";
    setState(newState);
    const hadMeasure = state.some((s) => s === "measure");
    const hasMeasure = newState.some((s) => s === "measure");
    if (!hasMeasure) {
      setSubGroupBy([]);
    } else if (hasMeasure && !hadMeasure && columns) {
      setSubGroupBy(columnsToSubGroupBy(columns));
    }
  };

  const characteristicsAll = useCharacteristics();

  const allOptions: string[] = useMemo<string[]>(() => {
    const stringCharacteristicsAll = characteristicsAll.map((item) =>
      typeof item === "string" ? item : String(item.value)
    );
    return [
      "investor",
      ...(support_investor_groups ? ["investorGroup"] : []),
      "deal",
      "measure",
      ...stringCharacteristicsAll
    ];
  }, [characteristicsAll, support_investor_groups]);

  const getGroupOptions = (index: number) => {
    const restGroups = state.filter((_, i) => i !== index);
    // Get all selected options across all groups
    const allSelectedOptions = restGroups.flat();
    // Filter out any option that is already selected in any group
    return allOptions.filter((option) => !allSelectedOptions.includes(option));
  };

  const enableSubGroupBy = useMemo<boolean>(() => {
    return state.some((item) => item === "measure");
  }, [state]);

  return (
    <Stack sx={{ maxWidth: 580, minWidth: 580, borderRadius: "8px", boxShadow: "sm" }}>
      <Stack
        sx={{
          borderBottom: "1px solid #dae0e2",
          p: "10px 30px",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between"
        }}
      >
        <Typography sx={{ fontSize: "18px", fontWeight: 400, lineHeight: "24px" }}>Group by</Typography>
        <IconButton onClick={onCancel}>
          <CloseIcon />
        </IconButton>
      </Stack>
      <Stack sx={{ gap: "24px", p: "20px 30px" }}>
        {Array.from({ length: groupsNumber }).map((_, index) => (
          <Autocomplete
            key={index}
            options={getGroupOptions(index)}
            renderInput={(params) => <TextField {...params} label={`Group ${index + 1}`} />}
            value={state[index]}
            getOptionLabel={(option) => camelCaseToTitle(option)}
            renderOption={(props, option) => <li {...props}>{startCase(option as string)}</li>}
            onChange={(_, value) => handleGroupChange(index, value)}
          />
        ))}
        {enableSubGroupBy && (
          <GroupedColumnsPicker
            value={subGroupBy}
            onChange={setSubGroupBy}
            selectedGroups={["measures", "calcFields"]}
          />
        )}
      </Stack>
      <Stack
        sx={{
          flexDirection: "row",
          gap: "8px",
          justifyContent: "flex-end",
          p: "20px 30px",
          borderTop: "1px solid #dae0e2"
        }}
      >
        <Button variant="outlined" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="sm"
          onClick={() => {
            const groupByFiltered = state.filter(Boolean);
            const { measures, calcFields } = enableSubGroupBy
              ? subGroupByToColumns(subGroupBy)
              : { measures: columns?.measures ?? [], calcFields: columns?.calcFields ?? [] };
            onSubmit({ groupBy: groupByFiltered, measures, calcFields });
          }}
        >
          Okay
        </Button>
      </Stack>
    </Stack>
  );
};
